import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useTenantContext } from '../../context/TenantContext';
import { ArrowLeft, Trash2, BookOpen, CreditCard, Loader2, Clock, Users, Star, Sparkles, Crown, Diamond, Gem, Wrench, Server, Code, HardDrive, Shield, Award, Zap, Cloud } from 'lucide-react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  useElearningCartStore, 
  useElearningCartStats, 
  useElearningCartActions,
  formatPrice,
  formatDifficultyLevel,
  formatCourseDuration
} from '../../stores/useElearningCartStore';

// Mapping d'icônes pour personnalisation
const iconMap: Record<string, React.ElementType> = {
  BookOpen,
  Sparkles,
  Star,
  Diamond,
  Gem,
  Crown,
  Wrench,
  Server,
  Code,
  HardDrive,
  Shield,
  Award,
  Zap,
  Cloud,
};

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const fadeIn = { 
  hidden: { opacity: 0, y: 20 }, 
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
};

const ElearningCartPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { tenant, previewConfig } = useTenantContext();

  // Thème E-learning (preview > tenant)
  const elearningTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'BookOpen', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  
  const { items, isLoading } = useElearningCartStore();
  const { itemCount, totalAmount, isEmpty } = useElearningCartStats();
  const { removeItem, clearCart } = useElearningCartActions();
  
  const [isPageLoading, setIsPageLoading] = useState(true);

  useEffect(() => {
    // Simuler le chargement initial
    const timer = setTimeout(() => {
      setIsPageLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Supprimer un cours du panier
  const handleRemoveItem = (courseId: string, courseTitle: string) => {
    removeItem(courseId);
    toast({
      title: 'Cours retiré',
      description: `${courseTitle} a été retiré de votre panier`,
      variant: 'default'
    });
  };

  // Vider le panier
  const handleClearCart = () => {
    clearCart();
    toast({
      title: 'Panier vidé',
      description: 'Tous les cours ont été retirés de votre panier',
      variant: 'default'
    });
  };

  // Passer à la page de checkout
  const proceedToCheckout = () => {
    if (isEmpty) {
      toast({
        title: 'Panier vide',
        description: 'Votre panier est vide. Ajoutez des cours avant de finaliser votre commande.',
        variant: 'destructive'
      });
      return;
    }
    
    navigate('/elearning/checkout');
  };

  if (isPageLoading) {
    return (
      <PublicLayout>
        <div className="min-h-screen relative overflow-hidden">
          {/* Fond avec la nouvelle palette de couleurs SIS */}
          <div 
            className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
              backgroundAttachment: 'fixed'
            }}
          >
            {elearningTheme.overlay.enabled && (
              <div className="absolute inset-0 backdrop-blur-[2px]" style={{
                backgroundImage: elearningTheme.overlay.useGradient
                  ? `linear-gradient(${(
                      {
                        'to-b': 'to bottom',
                        'to-t': 'to top',
                        'to-r': 'to right',
                        'to-l': 'to left',
                        'to-tr': 'to top right',
                        'to-br': 'to bottom right',
                        'to-tl': 'to top left',
                        'to-bl': 'to bottom left',
                      } as Record<string, string>
                    )[elearningTheme.overlay.orientation] || 'to bottom'},
                      ${hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity1)},
                      ${hexToRgba(elearningTheme.overlay.color2, elearningTheme.overlay.opacity2)},
                      ${hexToRgba(elearningTheme.overlay.color3, elearningTheme.overlay.opacity3)}
                    )`
                  : undefined,
                backgroundColor: !elearningTheme.overlay.useGradient
                  ? hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity2)
                  : undefined,
              }} />
            )}
          </div>
          
          <div className="flex flex-col justify-center items-center h-screen relative z-10">
            <div className="relative">
              <div className="w-16 h-16 border-4 rounded-full animate-spin"
                style={{
                  borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2),
                  borderTopColor: tenant?.config?.color1 || '#fe8d17'
                }}
              ></div>
              <div className="absolute inset-0 w-16 h-16 border-4 border-transparent rounded-full animate-spin" 
                style={{ 
                  borderTopColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.3),
                  animationDirection: 'reverse',
                  animationDuration: '1.5s'
                }}
              ></div>
            </div>
            <span className="mt-6 text-xl font-medium" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Chargement du panier...</span>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEOMetaTags 
        title="Mon Panier E-Learning"
        description="Consultez et gérez les cours dans votre panier avant de finaliser votre commande."
        keywords="panier, cours, e-learning, commande"
      />
      {/* Fond avec la nouvelle palette de couleurs SIS */}
      <div 
        className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
        style={{
          backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
          backgroundAttachment: 'fixed'
        }}
      >
        {elearningTheme.overlay.enabled && (
          <div className="absolute inset-0 backdrop-blur-[2px]" style={{
            backgroundImage: elearningTheme.overlay.useGradient
              ? `linear-gradient(${(
                  {
                    'to-b': 'to bottom',
                    'to-t': 'to top',
                    'to-r': 'to right',
                    'to-l': 'to left',
                    'to-tr': 'to top right',
                    'to-br': 'to bottom right',
                    'to-tl': 'to top left',
                    'to-bl': 'to bottom left',
                  } as Record<string, string>
                )[elearningTheme.overlay.orientation] || 'to bottom'},
                  ${hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity1)},
                  ${hexToRgba(elearningTheme.overlay.color2, elearningTheme.overlay.opacity2)},
                  ${hexToRgba(elearningTheme.overlay.color3, elearningTheme.overlay.opacity3)}
                )`
              : undefined,
            backgroundColor: !elearningTheme.overlay.useGradient
              ? hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity2)
              : undefined,
          }} />
        )}
      </div>

      {/* Particules flottantes avec couleurs personnalisables */}
      <div className="absolute inset-0 overflow-hidden z-10">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.08) }} />
        <div className="absolute top-3/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.08), animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-3/4 w-48 h-48 rounded-full blur-2xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.15), animationDelay: '4s' }} />
      </div>
      
      {/* Particules flottantes avec couleurs et icônes personnalisables */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Top-Left */}
        {elearningTheme.icons[0]?.enabled && (
          <motion.div
            className="absolute top-20 left-10"
            style={{ color: hexToRgba(elearningTheme.icons[0].color, 0.2) }}
            animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          >
            {React.createElement(iconMap[elearningTheme.icons[0].icon] || BookOpen, { size: elearningTheme.icons[0].size })}
          </motion.div>
        )}
        {/* Top-Right */}
        {elearningTheme.icons[1]?.enabled && (
          <motion.div
            className="absolute top-40 right-20"
            style={{ color: hexToRgba(elearningTheme.icons[1].color, 0.2) }}
            animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          >
            {React.createElement(iconMap[elearningTheme.icons[1].icon] || Sparkles, { size: elearningTheme.icons[1].size })}
          </motion.div>
        )}
        {/* Bottom-Left */}
        {elearningTheme.icons[2]?.enabled && (
          <motion.div
            className="absolute bottom-40 left-20"
            style={{ color: hexToRgba(elearningTheme.icons[2].color, 0.2) }}
            animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          >
            {React.createElement(iconMap[elearningTheme.icons[2].icon] || Diamond, { size: elearningTheme.icons[2].size })}
          </motion.div>
        )}
        {/* Bottom-Right */}
        {elearningTheme.icons[3]?.enabled && (
          <motion.div
            className="absolute bottom-20 right-10"
            style={{ color: hexToRgba(elearningTheme.icons[3].color, 0.2) }}
            animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          >
            {React.createElement(iconMap[elearningTheme.icons[3].icon] || Star, { size: elearningTheme.icons[3].size })}
          </motion.div>
        )}
      </div>
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-24 md:pb-20 z-20">
        <div className="mb-8 py-16 text-center">
          <h1 className="text-4xl font-bold mb-4 drop-shadow-lg" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Mon Panier</h1>
          <p className="text-lg drop-shadow-md" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.9) }}>
            Vous avez <span style={{ color: tenant?.config?.color1 || '#fe8d17' }} className="font-bold">{itemCount}</span> cours{items.length !== 1 ? 's' : ''} dans votre panier
          </p>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {!isEmpty ? (
            <motion.div 
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
              variants={fadeIn}
              initial="hidden"
              animate="visible"
            >
              <div className="lg:col-span-2">
                <Card className="bg-white/5 border backdrop-blur-sm shadow-xl text-white"
                  style={{
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                  }}
                >
                  <CardHeader className="border-b" style={{ borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2) }}>
                    <CardTitle className="flex items-center" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                      <BookOpen className="mr-2" style={{ color: tenant?.config?.color1 || '#fe8d17' }} size={24} />
                      Cours ({itemCount})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y" style={{ borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2) }}>
                      {items.map((item) => (
                        <div key={item.course_id} className="p-6 hover:bg-white/5 transition-colors">
                          <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-shrink-0">
                              <div className="w-full sm:w-32 h-20 rounded-lg overflow-hidden border"
                                style={{
                                  backgroundImage: `linear-gradient(to bottom right, ${hexToRgba(tenant?.config?.color2 || '#008aef', 0.2)}, ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)})`,
                                  borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)
                                }}
                              >
                                {item.thumbnail_url ? (
                                  <img
                                    src={item.thumbnail_url}
                                    alt={item.title}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <BookOpen size={24} style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.5) }} />
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex-grow">
                              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                                <div className="flex-grow">
                                  <h3 className="text-lg font-semibold mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{item.title}</h3>
                                  <div className="flex flex-wrap items-center gap-4 text-sm mb-3" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                                    {item.instructor_name && (
                                      <div className="flex items-center">
                                        <Users size={14} className="mr-1" />
                                        {item.instructor_name}
                                      </div>
                                    )}
                                    {item.duration_hours && (
                                      <div className="flex items-center">
                                        <Clock size={14} className="mr-1" />
                                        {formatCourseDuration(item.duration_hours)}
                                      </div>
                                    )}
                                    {item.difficulty_level && (
                                      <div className="flex items-center">
                                        <Star size={14} className="mr-1" />
                                        {formatDifficultyLevel(item.difficulty_level)}
                                      </div>
                                    )}
                                    {item.category_name && (
                                      <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white mb-3"
                                           style={{ backgroundColor: item.category_color || tenant?.config?.color2 || '#008aef' }}>
                                        {item.category_name}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-3">
                                  <div className="text-xl font-bold" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>
                                    {formatPrice(item.price, item.currency)}
                                  </div>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => handleRemoveItem(item.course_id, item.title)}
                                    className="text-red-500 hover:bg-red-700/70 hover:text-white hover:border-red-50 transition-all duration-200 font-medium"
                                  >
                                    <Trash2 size={24} className="mr-1" />                            
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                  <CardFooter className="p-4 flex flex-col sm:flex-row justify-between gap-4">
                    <Button 
                      variant="outline" 
                      onClick={handleClearCart}
                      className="border-red-500/70 text-white bg-red-500 hover:bg-red-500/70 hover:text-white hover:border-red-500 transition-all duration-200 font-medium px-6"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Vider le panier
                    </Button>
                    <Button asChild variant="outline" className="transition-all duration-200 font-medium px-6"
                      style={{
                        borderColor: `${tenant?.config?.color1 || '#fe8d17'}b3`,
                        color: tenant?.config?.color3 || '#ffffff',
                        backgroundColor: tenant?.config?.color1 || '#fe8d17'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.7);
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = tenant?.config?.color1 || '#fe8d17';
                      }}
                    >
                      <Link to="/elearning/courses">
                        <BookOpen size={16} className="mr-2" />
                        Continuer les achats
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
              
              {/* Résumé de la commande */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <Card className="bg-white/5 border backdrop-blur-sm shadow-xl text-white"
                    style={{
                      borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                    }}
                  >
                    <CardHeader>
                      <CardTitle style={{ color: tenant?.config?.color3 || '#ffffff' }}>Résumé de la commande</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-between" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                        <span>Nombre de cours</span>
                        <span>{itemCount}</span>
                      </div>
                      <div className="flex justify-between" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                        <span>Sous-total</span>
                        <span>{formatPrice(totalAmount)}</span>
                      </div>
                      <div className="flex justify-between" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                        <span>Accès</span>
                        <span>Immédiat</span>
                      </div>
                      <div className="border-t pt-4 flex justify-between font-bold text-lg" style={{ borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2) }}>
                        <span style={{ color: tenant?.config?.color3 || '#ffffff' }}>Total</span>
                        <span style={{ color: tenant?.config?.color1 || '#fe8d17' }}>{formatPrice(totalAmount)}</span>
                      </div>
                      
                      {/* Avantages */}
                      <div className="border rounded-lg p-4 mt-4"
                        style={{
                          backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1),
                          borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)
                        }}
                      >
                        <h4 className="font-semibold mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Inclus dans votre achat :</h4>
                        <ul className="text-sm space-y-1" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                          <li>• Accès à vie aux cours</li>
                          <li>• Certificat de completion</li>
                          <li>• Support de l'instructeur</li>
                          <li>• Accès mobile et desktop</li>
                        </ul>
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button 
                        onClick={proceedToCheckout} 
                        size="lg" 
                        className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                        style={{
                          backgroundColor: tenant?.config?.color1 || '#fe8d17',
                          color: tenant?.config?.color3 || '#ffffff',
                          backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                          borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4),
                          boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                        }}
                      >
                        <CreditCard size={20} className="mr-2" />
                        Finaliser l'achat
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div 
              className="text-center py-20 bg-white/5 border backdrop-blur-sm rounded-xl shadow-xl"
              style={{
                borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
              }}
              variants={fadeIn}
              initial="hidden"
              animate="visible"
            >
              <BookOpen size={48} className="mx-auto mb-6" style={{ color: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.5) }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Votre panier est vide</h2>
              <p className="mb-8" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>
                Explorez notre catalogue de cours pour commencer votre apprentissage.
              </p>
              <Button asChild size="lg" className="shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                style={{
                  backgroundColor: tenant?.config?.color1 || '#fe8d17',
                  color: tenant?.config?.color3 || '#ffffff',
                  backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                  borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4),
                  boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                }}
              >
                <Link to="/elearning/courses">
                  <ArrowLeft size={20} className="mr-2" />
                  Parcourir les cours
                </Link>
              </Button>
            </motion.div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default ElearningCartPage;
